/* eslint-disable @typescript-eslint/no-unused-vars */
import { CSSProperties } from 'react';
import { StylesConfig, OptionProps } from 'react-select';
import styled from 'styled-components';

import colors from 'utils/colors';
import device from 'utils/device';

import { State, OptionType } from './interfaces';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: stretch;
  max-width: 350px;
  width: 100%;

  @media ${device.isMobile} {
    max-width: none;
  }

  > div {
    width: inherit;
    max-width: inherit;
  }
`;

export const Label = styled.label`
  align-self: stretch;
  font-size: 16px;
  line-height: 24px;
  font-family: VIC;
  font-weight: 600;
  overflow-wrap: break-word;
  margin-bottom: 8px;
  color: ${colors.blueGrey};
`;

export const LoadingIndicatorWrapper = styled.div`
  margin: 7px 10px 0;
`;

export const ClearIndicatorWrapper = styled.div`
  width: 13px;
  height: 22px;
  margin-right: 10px;
`;

export const Option = styled.div<{ isFocused: boolean }>`
  font-family: VIC;
  background-color: ${props => (props.isFocused ? colors.blueGrey : 'transparent')};
  color: ${props => (props.isFocused ? colors.white : colors.blueGrey)};
  font-size: ${props => (props.isFocused ? '1.125rem' : '1rem')};
  line-height: 1.25;
  border-bottom: 1px solid ${colors.darkGrey25};
  padding: 16px 20px;
  -webkit-tap-highlight-color: ${colors.blueGrey};
`;

/**
 "react-select" encourage to use the new Styles API, however it still have the option of styling via CSS classes (This ensures compatibility with styled components, CSS modules and other libraries). https://react-select.com/styles#using-classnames.
  <div class="react-select-container">
    <div class="react-select__control">
      <div class="react-select__value-container">...</div>
      <div class="react-select__indicators">...</div>
    </div>
    <div class="react-select__menu">
      <div class="react-select__menu-list">
        <div class="react-select__option">...</div>
      </div>
    </div>
  </div>
 */
/**
 * Return styling result of the <AsyncSelect /> element
 * @param hasError does the element has error result
 */
export function Select(hasError: boolean): StylesConfig {
  return {
    control: (base, state) => {
      return {
        ...base,
        alignSelf: 'stretch',
        height: '48px',
        borderRadius: '2px',
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '-0.32px',
        outline: '0px !important',
        color: colors.blueGrey,
        backgroundColor: state.isDisabled ? colors.darkGrey10 : colors.white,
        boxShadow: 'none',
        border: `1px solid ${
          hasError
            ? colors.red
            : state.isDisabled
            ? colors.darkGrey75
            : state.isFocused
            ? colors.orange
            : colors.blueGrey
        }`,
        width: '100%',
        '&:hover': {
          borderColor: 'none'
        }
      };
    },
    input: (base, state) => {
      return {
        ...base,
        fontFamily: 'VIC',
        '& input': {
          font: 'inherit'
        }
      };
    },
    valueContainer: (base, state) => {
      return {
        ...base,
        padding: '0 16px'
      };
    },
    menu: (base, state) => {
      return {
        ...base,
        width: '100%',
        borderRadius: '2px',
        backgroundColor: `${colors.darkGrey10}`,
        marginBottom: 0,
        marginTop: 0
      };
    },
    option: (base, state) => {
      return {
        ...base,
        backgroundColor: state.isFocused ? `${colors.blueGrey}` : 'transparent',
        color: state.isFocused ? `${colors.white}` : `${colors.blueGrey}`,
        fontSize: state.isFocused ? '1.125rem' : '1rem',
        lineHeight: 1.25,
        borderBottom: `1px solid ${colors.darkGrey25}`,
        padding: '16px 20px',
        WebkitTapHighlightColor: `${colors.blueGrey}`
      };
    },
    dropdownIndicator: (base, state) => {
      return {
        ...base,
        zIndex: 1,
        display: 'none'
      };
    },
    indicatorSeparator: (base, state) => {
      return {
        ...base,
        display: 'none'
      };
    },
    // https://github.com/JedWatson/react-select/issues/1351#issuecomment-505148882
    singleValue: (base, state) => {
      return {
        ...base,
        display: state.selectProps.menuIsOpen ? 'none' : 'block',
        color: colors.blueGrey
      };
    }
  };
}

export const ErrorWrapper = styled.div`
  > div {
    margin-top: 8px;
    justify-content: flex-start;
  }
`;

export const HintLabel = styled(Label)`
  font-weight: 400;
  margin-top: 8px;
  color: ${colors.blueGrey75};
`;
